import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { format } from "date-fns";
import { ConsultationUsage } from "@/lib/settings/billingData";
import { ServiceType } from "../../enum/ServiceType.js";
import { ConsultationStatus } from "../../enum/ConsultationStatus.js";
import {
  Activity,
  FileText,
  Syringe,
  Pill,
  Brain,
  MessageSquare,
  CheckCircle,
  Trash2,
  AlertCircle,
  Download,
  ChevronDown,
  ChevronUp,
  Calendar,
} from "lucide-react";

interface ConsultationChargesModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  consultations: ConsultationUsage[];
  consultationFee: number;
  isDarkMode?: boolean;
}

const getTextColor = (
  isDarkMode: boolean,
  variant: "primary" | "secondary" | "muted" = "primary",
) => {
  if (variant === "secondary")
    return isDarkMode ? "text-gray-300" : "text-gray-600";
  if (variant === "muted")
    return isDarkMode ? "text-gray-400" : "text-gray-500";
  return isDarkMode ? "text-white" : "text-gray-900";
};

export function ConsultationChargesModal({
  isOpen,
  onOpenChange,
  consultations,
  consultationFee,
  isDarkMode = false,
}: ConsultationChargesModalProps) {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [expandedConsultations, setExpandedConsultations] = useState<
    Set<string>
  >(new Set());
const chargedConsultations = useMemo(
  () =>
    (Array.isArray(consultations) ? consultations : [consultations])
      .flatMap(c => c?.details || []),
  [consultations]
);
 const filteredConsultations = useMemo(() => {
  let filtered = chargedConsultations;

  const getDate = (c) =>
    c.billingInfo?.createdOn
      ? new Date(c.billingInfo.createdOn)
      : c.createdOn
      ? new Date(c.createdOn)
      : null;

  if (startDate) {
    filtered = filtered.filter((c) => {
      const d = getDate(c);
      return d && d >= new Date(startDate);
    });
  }

  if (endDate) {
    const endDateTime = new Date(endDate);
    endDateTime.setHours(23, 59, 59, 999);

    filtered = filtered.filter((c) => {
      const d = getDate(c);
      return d && d <= endDateTime;
    });
  }

  return filtered;
}, [chargedConsultations, startDate, endDate]);

  const totalCharges = useMemo(
    () => filteredConsultations.length * consultationFee,
    [filteredConsultations, consultationFee],
  );

  const toggleExpanded = (id: string) => {
    setExpandedConsultations((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

const getChargeReasonLabel = (billingInfo) => {
  if (!billingInfo) return null;

   if (billingInfo?.transcription?.transcriptionDuration > 0 ||billingInfo?.isSummaryGenerated) {
      return "Generated transcription summary";
    }

  if (billingInfo.consultationStatus === 1) {
    return "Consultation Completed";
  }

  const clinicalActions = ["prescription", "referralletter", "vaccination"];

  const hasClinicalAction =
    Array.isArray(billingInfo?.actionsUsed) &&
    billingInfo.actionsUsed.some(a =>
      clinicalActions.includes(a.actionName?.toLowerCase())
    );

  if (hasClinicalAction) {
    return "Consultation performed with clinical actions";
  }

  return null;
};


  const handleExportCSV = () => {
    const headers = [
      "Date",
      "Patient Name",
      "Service",
      "Type",
      "Status",
      "Charge Reason",
      "Deleted",
      "Transcription Summary",
      "DemDx Used",
      "Prescription Created",
      "Letter Generated",
      "Vaccination Recorded",
      "Actions Summary",
      "Ask Me Anything Count",
    ];

   const rows = filteredConsultations.map((c) => {
  const billing = c.billingInfo || {};

  return [
    format(new Date(c.createdOn), "yyyy-MM-dd HH:mm"),
    c.patientName || "-",
    c.serviceName,
    ServiceType[billing.serviceType] || "-",
    ConsultationStatus[billing.consultationStatus] || "-",
    getChargeReasonLabel(billing) || "-",
    billing.consultationStatus === 0 ? "Yes" : "No",
    (billing.transcription?.transcriptionDuration > 0 || billing?.isSummaryGenerated) ? "Yes" : "No",
    billing.isDemDx ? "Yes" : "No",
    billing.actionsUsed?.some(a => a.actionName === "prescription") ? "Yes" : "No",
    billing.actionsUsed?.some(a => a.actionName === "referralletter") ? "Yes" : "No",
    billing.actionsUsed?.some(a => a.actionName === "vaccination") ? "Yes" : "No",
    billing.actionsUsed?.length > 0 ? "Yes" : "No",
    billing.aiCalls || "0",
  ];
});


    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `consultation-charges-${format(new Date(), "yyyy-MM-dd")}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const clearFilters = () => {
    setStartDate("");
    setEndDate("");
  };

const renderChargeableActions = (billingInfo) => {
  if (!billingInfo) return null;

  const actionsList = [];

  if (billingInfo?.transcription?.transcriptionDuration > 0) {
    actionsList.push(
      <div key="transcription" className="flex items-center gap-2 text-xs py-1">
        <FileText className="w-3 h-3 text-blue-500" />
        <span className={getTextColor(isDarkMode, "secondary")}>
          Generated transcription summary
        </span>
      </div>
    );
  }


  if (billingInfo?.consultationChat && billingInfo?.consultationChat.trim() !== "") {
    actionsList.push(
      <div key="ask-anything" className="flex items-center gap-2 text-xs py-1">
        <MessageSquare className="w-3 h-3 text-orange-500" />
        <span className={getTextColor(isDarkMode, "secondary")}>
          Ask me anything used ({billingInfo?.consultationChat} times)
        </span>
      </div>
    );
  }
  if (billingInfo?.isDemDx) {
    actionsList.push(
      <div key="demdx" className="flex items-center gap-2 text-xs py-1">
        <Brain className="w-3 h-3 text-purple-500" />
        <span className={getTextColor(isDarkMode, "secondary")}>
          DemDx was used
        </span>
      </div>
    );
  }

  if (Array.isArray(billingInfo?.actionsUsed)) {
    billingInfo.actionsUsed.forEach((action) => {
      switch (action.actionName.toLowerCase()) {
        case "prescription":
          actionsList.push(
            <div key="prescription" className="flex items-center gap-2 text-xs py-1">
              <Pill className="w-3 h-3 text-green-500" />
              <span className={getTextColor(isDarkMode, "secondary")}>
                Prescription created
              </span>
            </div>
          );
          break;
        case "referralletter":
          actionsList.push(
            <div key="referralletter" className="flex items-center gap-2 text-xs py-1">
              <FileText className="w-3 h-3 text-indigo-500" />
              <span className={getTextColor(isDarkMode, "secondary")}>
                Letter generated
              </span>
            </div>
          );
          break;
        case "vaccination":
          actionsList.push(
            <div key="vaccination" className="flex items-center gap-2 text-xs py-1">
              <Syringe className="w-3 h-3 text-red-500" />
              <span className={getTextColor(isDarkMode, "secondary")}>
                Vaccination recorded
              </span>
            </div>
          );
          break;
        default:
          break; 
      }
    });
  }

  return <div className="space-y-1">{actionsList}</div>;
};


  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={`max-w-3xl max-h-[90vh] h-auto overflow-hidden flex flex-col transition-colors duration-300 ${
          isDarkMode
            ? "bg-gray-800 border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle
              className={`flex items-center gap-2 ${getTextColor(isDarkMode)}`}
            >
              <Activity className="w-5 h-5" />
              Chargeable Consultations Breakdown
            </DialogTitle>
            <Button
              onClick={handleExportCSV}
              variant="outline"
              size="sm"
              className={`flex items-center gap-2 ${isDarkMode ? "border-gray-600 text-gray-300 hover:bg-gray-700" : ""}`}
            >
              <Download className="w-4 h-4" />
              Export CSV
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-y-auto">
          {/* Date Filter Section */}
          <div
            className={`p-4 rounded-lg border flex-shrink-0 ${isDarkMode ? "bg-gray-700 border-gray-600" : "bg-gray-50 border-gray-200"}`}
          >
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4" />
              <h3 className={`text-sm font-medium ${getTextColor(isDarkMode)}`}>
                Filter by Date Range
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label
                  htmlFor="start-date"
                  className={`text-xs ${getTextColor(isDarkMode, "secondary")}`}
                >
                  From Date
                </Label>
                <Input
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={
                    isDarkMode ? "bg-gray-600 border-gray-500 text-white" : ""
                  }
                />
              </div>
              <div className="space-y-1">
                <Label
                  htmlFor="end-date"
                  className={`text-xs ${getTextColor(isDarkMode, "secondary")}`}
                >
                  To Date
                </Label>
                <Input
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className={
                    isDarkMode ? "bg-gray-600 border-gray-500 text-white" : ""
                  }
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs opacity-0">Actions</Label>
                <Button
                  onClick={clearFilters}
                  variant="outline"
                  size="sm"
                  className={`w-full ${isDarkMode ? "border-gray-600 text-gray-300 hover:bg-gray-700" : ""}`}
                  disabled={!startDate && !endDate}
                >
                  Clear Filters
                </Button>
              </div>
            </div>
            {(startDate || endDate) && (
              <div
                className={`mt-2 text-xs ${getTextColor(isDarkMode, "muted")}`}
              >
                Showing {filteredConsultations?.length} of{" "}
                {chargedConsultations?.length} consultations
              </div>
            )}
          </div>

        

          {/* Consultations List */}
          <div className="space-y-3 overflow-y-auto flex-1 pr-2 min-h-0">
            {filteredConsultations?.length === 0 ? (
              <div
                className={`text-center py-8 ${getTextColor(isDarkMode, "muted")}`}
              >
                No chargeable consultations in this period
              </div>
            ) : (
              filteredConsultations?.map((consultation) => (
                <div
                  key={consultation.id}
                  className={`border rounded-lg p-4 transition-colors duration-300 ${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600"
                      : "bg-white border-gray-200"
                  } ${
                     consultation?.billingInfo?.consultationStatus===0
                      ? isDarkMode
                        ? "border-orange-700 bg-orange-900/20"
                        : "border-orange-200 bg-orange-50"
                      : ""
                  }`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4
                          className={`font-medium ${getTextColor(isDarkMode)}`}
                        >
                          {consultation?.serviceName}
                        </h4>
                        {consultation?.billingInfo?.consultationStatus ===0&& (
                          <Badge
                            variant="outline"
                            className={`${
                              isDarkMode
                                ? "border-orange-600 text-orange-400"
                                : "border-orange-600 text-orange-600"
                            }`}
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Deleted
                          </Badge>
                        )}
                        <Badge
                          variant="outline"
                          className={`${
                            consultation?.billingInfo?.serviceType === 1
                              ? isDarkMode
                                ? "border-purple-600 text-purple-400"
                                : "border-purple-600 text-purple-600"
                              : isDarkMode
                                ? "border-green-600 text-green-400"
                                : "border-green-600 text-green-600"
                          }`}
                        >
                          {ServiceType[consultation?.billingInfo?.serviceType]}
                        </Badge>
                      </div>
                      <div
                        className={`text-sm ${getTextColor(isDarkMode, "secondary")}`}
                      >
                        Patient: {consultation.patientName}
                      </div>
                      <div
                        className={`text-xs ${getTextColor(isDarkMode, "muted")}`}
                      >
                         {consultation?.billingInfo?.createdOn &&
                        !isNaN(new Date(consultation?.billingInfo.createdOn).getTime())
                            ? format(
                                new Date(consultation?.billingInfo?.createdOn),
                                "MMM dd, yyyy 'at' HH:mm"
                            )
                            : "Date not available"}
                      </div>
                    </div>
                  </div>

                  {/* Expandable Charge Reason & Actions */}
                  {consultation.billingInfo  && (
                    <div
                      className={`border-t ${isDarkMode ? "border-gray-600" : "border-gray-200"} pt-3 mt-3`}
                    >
                      <button
                        onClick={() => toggleExpanded(consultation?.billingInfo?.consultationId)}
                        className={`flex items-center justify-between w-full text-sm font-medium ${getTextColor(isDarkMode)} hover:opacity-80 transition-opacity`}
                      >
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-blue-500" />
                          Charge Details
                        </div>
                        {expandedConsultations.has(consultation?.billingInfo?.consultationId) ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>

                      {expandedConsultations.has(consultation?.billingInfo?.consultationId) && (
                        <div className="mt-3 space-y-3">
                          {/* Charge Reason */}
                          {consultation?.billingInfo && (
                            <div
                              className={`p-3 rounded-md ${
                                isDarkMode
                                  ? "bg-gray-600 border border-gray-500"
                                  : "bg-gray-100 border border-gray-200"
                              }`}
                            >
                              <div
                                className={`text-xs font-medium mb-1 ${getTextColor(isDarkMode, "muted")}`}
                              >
                                Charge Reason
                              </div>
                              <div
                                className={`text-sm ${getTextColor(isDarkMode, "secondary")}`}
                              >
                                {getChargeReasonLabel(
                                  consultation?.billingInfo,
                                )}
                              </div>
                            </div>
                          )}

                          {/* Chargeable Actions */}
                          {consultation?.billingInfo && (
                            <div>
                              <div
                                className={`text-xs font-medium mb-2 ${getTextColor(isDarkMode, "secondary")}`}
                              >
                                Actions that triggered the charge:
                              </div>
                               {renderChargeableActions(consultation?.billingInfo)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
